import { useDoraOverview } from '@/hooks/use-products';
import {
  LineChart, Line, ResponsiveContainer,
} from 'recharts';

const TIER_COLORS: Record<string, { bg: string; text: string }> = {
  elite: { bg: 'rgba(34,197,94,0.12)', text: '#22C55E' },
  high: { bg: 'rgba(96,165,250,0.12)', text: '#60A5FA' },
  medium: { bg: 'rgba(245,158,11,0.12)', text: '#F59E0B' },
  low: { bg: 'rgba(239,68,68,0.12)', text: '#EF4444' },
  none: { bg: 'rgba(160,160,160,0.08)', text: '#6A6A6A' },
};

function TierBadge({ tier }: { tier: string }) {
  const colors = TIER_COLORS[tier] ?? TIER_COLORS.none;
  return (
    <span
      className="text-[0.62rem] font-bold px-2.5 py-0.5 rounded-full uppercase tracking-wider"
      style={{ background: colors.bg, color: colors.text }}
    >
      {tier === 'none' ? '—' : tier}
    </span>
  );
}

function DoraCard({ label, value, unit, tier, target, trendData, trendColor }: {
  label: string;
  value: number;
  unit: string;
  tier: string;
  target: string;
  trendData?: number[];
  trendColor?: string;
}) {
  const sparkData = trendData?.map((v, i) => ({ x: i, y: v })) ?? [];

  return (
    <div className="bg-bg-card border border-border rounded-lg p-5">
      <div className="text-[0.72rem] font-semibold text-text-muted mb-2">{label}</div>
      <div className="flex items-baseline gap-1 mb-2">
        <span className="font-display text-3xl font-bold tracking-tight">
          {tier === 'none' ? '—' : value}
        </span>
        {tier !== 'none' && (
          <span className="text-[0.75rem] text-text-muted">{unit}</span>
        )}
      </div>
      <div className="mb-3">
        <TierBadge tier={tier} />
      </div>
      <div className="text-[0.68rem] text-text-muted mb-3">Target: {target}</div>
      {sparkData.length > 1 && (
        <div className="h-10">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={sparkData}>
              <Line
                type="monotone"
                dataKey="y"
                stroke={trendColor ?? '#009090'}
                strokeWidth={1.5}
                dot={false}
                fill={`${trendColor ?? '#009090'}1A`}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

export function DoraPage() {
  const { data: doraData, isLoading } = useDoraOverview();

  if (isLoading) {
    return (
      <div>
        <div className="mb-8">
          <div className="h-7 w-48 bg-bg-elevated rounded animate-pulse mb-1" />
          <div className="h-4 w-72 bg-bg-elevated rounded animate-pulse" />
        </div>
        <div className="grid grid-cols-[repeat(auto-fit,minmax(220px,1fr))] gap-4 mb-8">
          {Array.from({ length: 4 }, (_, i) => (
            <div key={i} className="bg-bg-card border border-border rounded-lg p-5 h-40 animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  const primary = doraData?.[0];
  const metrics = primary?.metrics;
  const weeklyTrend = primary?.weekly_trend ?? [];

  const freqTrend = weeklyTrend.map(w => w.deploys / 7);
  const failTrend = weeklyTrend.map(w => w.deploys > 0 ? (w.failures / w.deploys) * 100 : 0);

  return (
    <div>
      <div className="mb-8">
        <h1 className="font-display text-2xl font-bold tracking-tight">DORA Metrics</h1>
        <p className="text-text-secondary text-[0.82rem] mt-1">
          Engineering performance across the ecosystem — last 30 days
        </p>
      </div>

      {metrics ? (
        <>
          <div className="grid grid-cols-[repeat(auto-fit,minmax(220px,1fr))] gap-4 mb-8">
            <DoraCard
              label="Deployment Frequency"
              value={metrics.deployment_frequency.value}
              unit={metrics.deployment_frequency.unit}
              tier={metrics.deployment_frequency.tier}
              target={metrics.deployment_frequency.target}
              trendData={freqTrend}
              trendColor="#22C55E"
            />
            <DoraCard
              label="Lead Time for Changes"
              value={metrics.lead_time.value}
              unit={metrics.lead_time.unit}
              tier={metrics.lead_time.tier}
              target={metrics.lead_time.target}
              trendColor="#00ADAD"
            />
            <DoraCard
              label="Change Failure Rate"
              value={metrics.change_failure_rate.value}
              unit={metrics.change_failure_rate.unit}
              tier={metrics.change_failure_rate.tier}
              target={metrics.change_failure_rate.target}
              trendData={failTrend}
              trendColor="#22C55E"
            />
            <DoraCard
              label="Time to Restore"
              value={metrics.mttr.value}
              unit={metrics.mttr.unit}
              tier={metrics.mttr.tier}
              target={metrics.mttr.target}
              trendColor="#00ADAD"
            />
          </div>

          {/* Comparison Table */}
          <div className="bg-bg-card border border-border rounded-lg p-5">
            <h4 className="text-[0.85rem] font-semibold mb-4">DORA by Product</h4>
            <table className="w-full border-collapse text-[0.78rem]">
              <thead>
                <tr>
                  {['Product', 'Deploy Freq', 'Lead Time', 'Failure Rate', 'MTTR', 'Overall'].map(h => (
                    <th key={h} className="text-left text-[0.62rem] font-semibold uppercase tracking-wider text-text-muted px-3 py-2.5 border-b border-border">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {doraData?.map(row => {
                  const m = row.metrics;
                  const hasData = m.overall_tier !== 'none';
                  return (
                    <tr key={row.product.slug} className="hover:bg-bg-elevated">
                      <td className="px-3 py-2.5 border-b border-border-subtle font-semibold">{row.product.name}</td>
                      <td className="px-3 py-2.5 border-b border-border-subtle text-text-secondary">
                        {hasData ? `${m.deployment_frequency.value}/wk` : <span className="text-text-muted">No data</span>}
                      </td>
                      <td className="px-3 py-2.5 border-b border-border-subtle text-text-secondary">
                        {hasData && m.lead_time.tier !== 'none' ? `${m.lead_time.value} min` : <span className="text-text-muted">No data</span>}
                      </td>
                      <td className={`px-3 py-2.5 border-b border-border-subtle ${hasData && m.change_failure_rate.value < 5 ? 'text-success' : 'text-text-secondary'}`}>
                        {hasData ? `${m.change_failure_rate.value}%` : <span className="text-text-muted">No data</span>}
                      </td>
                      <td className="px-3 py-2.5 border-b border-border-subtle text-text-secondary">
                        {hasData && m.mttr.tier !== 'none' ? `${m.mttr.value} min` : <span className="text-text-muted">No data</span>}
                      </td>
                      <td className="px-3 py-2.5 border-b border-border-subtle">
                        {hasData ? <TierBadge tier={m.overall_tier} /> : <span className="text-text-muted">—</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      ) : (
        <div className="text-center py-16 text-text-muted">
          <div className="text-2xl mb-3 opacity-40">&#9632;</div>
          <h3 className="text-base text-text-secondary mb-1">No deploy data yet</h3>
          <p className="text-[0.78rem] max-w-[400px] mx-auto">
            DORA metrics are calculated from your deployment history. Start reporting deploys via the ingest API.
          </p>
        </div>
      )}
    </div>
  );
}
